import React, { useState, useEffect, useRef } from 'react';
import { fetchUtils } from 'react-admin';
import DataTable from 'react-data-table-component';
import { Card, CardContent, TextField as MuiTextField, Button, Typography,
  CircularProgress, Chip, Paper } from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import CloudDownload from '@material-ui/icons/CloudDownload';
import ReactMarkdown from 'react-markdown';
const ReactHighcharts = require('react-highcharts');

const fetch = fetchUtils.fetchJson;
const BASE_URL = window.nobid_endpoint + '/assistant';

const SUGGESTED_QUERIES = [
  { name: 'Revenue last 30 days', prompt: 'Daily revenue and impressions from Prebid for the last 30 days' },
  { name: 'GAM revenue by order', prompt: 'Revenue and impressions by order this month from GAM' },
  { name: 'Viewability by ad unit', prompt: 'Viewability rate by ad unit last 7 days from GAM' },
  { name: 'Fill rate last 7 days', prompt: 'Daily fill rate (impressions / ad_requests) from Prebid for the last 7 days' },
  { name: 'Revenue last 6 months', prompt: 'Monthly revenue comparison from Prebid for the last 6 months' },
  { name: 'Top bidders this month', prompt: 'Revenue and impressions by bidder this month from Prebid' },
  { name: 'Ad malware this week', prompt: 'Malware blocks by bidder this week' }
];

function formatNumber(val) {
  if (val === null || val === undefined || val === '') return '-';
  if (typeof val === 'number') return val.toLocaleString();
  return val;
}

function buildColumns(rows) {
  if (!rows || rows.length === 0) return [];
  const keys = Object.keys(rows[0]);
  return keys.map(key => ({
    name: key.replace(/_/g, ' '),
    selector: row => row[key],
    sortable: true,
    cell: row => <span>{formatNumber(row[key])}</span>,
    wrap: true
  }));
}

function exportCSV(rows) {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const header = keys.join(',');
  const body = rows.map(row => keys.map(k => {
    const val = row[k];
    if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
    return val;
  }).join(',')).join('\n');
  const csv = header + '\n' + body;
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `insights_report_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function getChartableMetrics(rows) {
  if (!rows || rows.length === 0) return [];
  return Object.keys(rows[0]).filter(k => {
    const v = rows[0][k];
    return typeof v === 'number' || (typeof v === 'string' && v !== '' && !isNaN(v) && !/^\d{4}-/.test(v));
  });
}

function buildChartConfig(rows, metadata, selectedMetric) {
  if (!rows || rows.length < 2) return null;
  const chartableMetrics = getChartableMetrics(rows);
  if (chartableMetrics.length === 0) return null;
  const metricKey = selectedMetric && chartableMetrics.includes(selectedMetric) ? selectedMetric : chartableMetrics[0];
  const metricLabel = (metricKey || '').replace(/_/g, ' ');

  // Time series
  const dateKey = Object.keys(rows[0]).find(k => /^date$|^ts/i.test(k) && typeof rows[0][k] === 'string');
  if (dateKey) {
    const sorted = [...rows].sort((a, b) => String(a[dateKey]).localeCompare(String(b[dateKey])));
    return {
      chart: { type: 'line', height: 250, backgroundColor: 'transparent' },
      title: { text: null },
      xAxis: { categories: sorted.map(r => {
        const val = r[dateKey];
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
          const d = new Date(val + 'T00:00:00');
          const day = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
          return `${day} ${val.slice(5)}`;
        }
        return val;
      }), labels: { rotation: -45, style: { fontSize: '10px' } } },
      yAxis: { title: { text: null } },
      legend: { enabled: false },
      credits: { enabled: false },
      series: [{ name: metricLabel, data: sorted.map(r => Number(r[metricKey]) || 0), color: '#1976d2' }]
    };
  }

  // Bar chart
  const dimKey = Object.keys(rows[0]).find(k => typeof rows[0][k] === 'string');
  if (!dimKey) return null;
  const top = rows.slice(0, 15);
  return {
    chart: { type: 'bar', height: Math.max(200, top.length * 28), backgroundColor: 'transparent' },
    title: { text: null },
    xAxis: { categories: top.map(r => String(r[dimKey] || '').substring(0, 40)), labels: { style: { fontSize: '11px' } } },
    yAxis: { title: { text: null } },
    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: { bar: { dataLabels: { enabled: true, formatter: function() { return this.y.toLocaleString(); } } } },
    series: [{ name: metricLabel, data: top.map(r => Number(r[metricKey]) || 0), color: '#1976d2' }]
  };
}

const ChatMessage = React.memo(function ChatMessage({ msg }) {
  const isUser = msg.role === 'user';
  const [chartMetric, setChartMetric] = useState(null);
  const chartableMetrics = !isUser && msg.rows && msg.rows.length > 0 ? getChartableMetrics(msg.rows) : [];

  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
      <Paper elevation={1} style={{
        padding: '12px 16px', maxWidth: '80%',
        backgroundColor: isUser ? '#1976d2' : '#f5f5f5',
        color: isUser ? '#fff' : '#333',
        borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px'
      }}>
        {isUser ? (
          <Typography variant="body1">{msg.text}</Typography>
        ) : (
          <ReactMarkdown>{msg.text}</ReactMarkdown>
        )}
        {msg.rows && msg.rows.length > 0 && (() => {
          const chartConfig = buildChartConfig(msg.rows, msg.metadata, chartMetric);
          return (
          <div style={{ marginTop: 12 }}>
            {chartConfig && (
              <div style={{ marginBottom: 12, background: '#fff', borderRadius: 4, padding: 8 }}>
                {chartableMetrics.length > 1 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                    {chartableMetrics.map(m => (
                      <Chip key={m} label={m.replace(/_/g, ' ').replace(/TOTAL LINE ITEM LEVEL /g, '')}
                        size="small" color={(chartMetric || chartableMetrics[0]) === m ? 'primary' : 'default'}
                        onClick={() => setChartMetric(m)} clickable />
                    ))}
                  </div>
                )}
                <ReactHighcharts config={chartConfig} />
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <Button size="small" startIcon={<CloudDownload />} onClick={() => exportCSV(msg.rows)} style={{ color: '#666' }}>
                Export CSV
              </Button>
            </div>
            <DataTable columns={buildColumns(msg.rows)} data={msg.rows} dense pagination paginationPerPage={10}
              highlightOnHover theme={window.nobid_table_theme ? window.nobid_table_theme() : undefined} />
          </div>
          );
        })()}
      </Paper>
    </div>
  );
});

export function InsightsAssistantPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function sendMessage(text) {
    if (!text || !text.trim() || loading) return;
    const userMsg = { role: 'user', text: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const history = messages.map(m => {
        if (m.role === 'user') return { role: 'user', content: m.text };
        const context = { summary: m.text };
        if (m.rows && m.rows.length > 0) { context.rows = m.rows.slice(0, 20); context.rowCount = m.rows.length; }
        return { role: 'assistant', content: JSON.stringify(context) };
      });
      const body = { message: text.trim(), history, context: {} };

      const { json } = await fetch(BASE_URL, {
        method: 'POST',
        headers: new Headers({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
        body: JSON.stringify(body)
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        text: json.summary || 'No response received.',
        rows: json.rows || [],
        metadata: json.metadata || null
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant', text: 'Sorry, something went wrong. Please try again.', rows: [], metadata: null
      }]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  }

  return (
    <Card style={{ margin: 16, height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px 16px 8px', borderBottom: '1px solid #eee' }}>
        <Typography variant="h6">Insights Assistant</Typography>
      </div>

      <CardContent style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {messages.length === 0 && !loading && (
          <div style={{ textAlign: 'center', paddingTop: 40 }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>Ask about your data</Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
              GAM reports, Prebid analytics, bidder performance, malware detection, and more
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8 }}>
              {SUGGESTED_QUERIES.map((q, i) => (
                <Chip key={i} label={q.name} clickable onClick={() => sendMessage(q.prompt)} variant="outlined" color="primary" />
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => <ChatMessage key={i} msg={msg} />)}
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0' }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">Analyzing...</Typography>
          </div>
        )}
        <div ref={chatEndRef} />
      </CardContent>

      <div style={{ padding: 16, borderTop: '1px solid #eee', display: 'flex', gap: 8 }}>
        <MuiTextField fullWidth variant="outlined" size="small" placeholder="Ask about your data..."
          value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={handleKeyPress} disabled={loading} autoFocus />
        <Button variant="contained" color="primary" onClick={() => sendMessage(input)} disabled={loading || !input.trim()}>
          <SendIcon />
        </Button>
      </div>
    </Card>
  );
}

export const InsightsAssistantList = InsightsAssistantPage;

// Embedded version for dashboard — no outer Card, accepts initialPrompt
export function InsightsAssistantEmbedded({ initialPrompt, onPromptConsumed }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);
  const initialPromptSent = useRef(false);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages]);

  // Auto-send initial prompt from dashboard chip
  useEffect(() => {
    if (initialPrompt && !initialPromptSent.current && !loading) {
      initialPromptSent.current = true;
      sendMessage(initialPrompt);
      if (onPromptConsumed) onPromptConsumed();
    }
  }, [initialPrompt]);

  async function sendMessage(text) {
    if (!text || !text.trim() || loading) return;
    const userMsg = { role: 'user', text: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const history = messages.map(m => {
        if (m.role === 'user') return { role: 'user', content: m.text };
        const context = { summary: m.text };
        if (m.rows && m.rows.length > 0) { context.rows = m.rows.slice(0, 20); context.rowCount = m.rows.length; }
        return { role: 'assistant', content: JSON.stringify(context) };
      });
      const body = { message: text.trim(), history, context: {} };

      const { json } = await fetch(BASE_URL, {
        method: 'POST',
        headers: new Headers({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
        body: JSON.stringify(body)
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        text: json.summary || 'No response received.',
        rows: json.rows || [],
        metadata: json.metadata || null
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant', text: 'Sorry, something went wrong. Please try again.', rows: [], metadata: null
      }]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
      <div style={{ flex: 1, overflow: 'auto', marginBottom: 8 }}>
        {messages.map((msg, i) => <ChatMessage key={i} msg={msg} />)}
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0' }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">Analyzing...</Typography>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <MuiTextField fullWidth variant="outlined" size="small" placeholder="Ask about your data..."
          value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={handleKeyPress} disabled={loading} />
        <Button variant="contained" color="primary" onClick={() => sendMessage(input)} disabled={loading || !input.trim()}>
          <SendIcon />
        </Button>
      </div>
    </div>
  );
}
